<!--
  User Menu 组件
  负责显示用户菜单和登录状态
  只消费 props，通过事件上抛操作
-->
<template>
  <div class="user-menu">
    <!-- 用户未登录时显示登录按钮 -->
    <template v-if="!isLoggedIn">
      <el-button type="primary" @click="handleShowLogin">登录</el-button>
      <LoginDialog
        v-model="showLoginDialog"
        @login-success="handleLoginSuccess"
      />
    </template>

    <!-- 用户已登录时显示用户信息 -->
    <template v-else>
      <el-dropdown
        trigger="hover"
        :hide-on-click="false"
        :show-arrow="false"
        :offset="12"
        popper-class="user-menu-dropdown-popper"
      >
        <div
          class="user-info"
          role="button"
          tabindex="0"
          @click.stop="handleAvatarClick"
          @keydown.enter.prevent="handleAvatarClick"
          @keydown.space.prevent="handleAvatarClick"
        >
          <el-avatar :size="34" :src="userInfo?.avatar || defaultAvatar">
            {{ userInfo?.username?.charAt(0)?.toUpperCase() }}
          </el-avatar>
          <el-icon class="dropdown-arrow"><ArrowDown /></el-icon>
        </div>
        <template #dropdown>
          <div class="modern-user-dropdown">
            <!-- 用户头像和基本信息 -->
            <div class="user-profile-section">
              <div class="user-avatar-wrapper">
                <el-avatar :size="56" :src="userInfo?.avatar || defaultAvatar" class="user-avatar">
                  {{ userInfo?.username?.charAt(0)?.toUpperCase() }}
                </el-avatar>
                <div class="user-status-dot"></div>
              </div>
              <div class="user-basic-info">
                <div class="user-name">{{ userInfo?.username }}</div>
                <div class="user-role-badge">{{ userRoleLabel }}</div>
              </div>
            </div>

            <!-- 配额信息卡片 -->
            <div class="quota-card">
              <div class="quota-header">
                <div class="quota-icon">
                  <el-icon><Star /></el-icon>
                </div>
                <div class="quota-title">今日配额</div>
              </div>
              <div class="quota-content">
                <div class="quota-numbers">
                  <span class="quota-used">{{ quotaUsed }}</span>
                  <span class="quota-separator">/</span>
                  <span class="quota-total">{{ quotaLimit }}</span>
                </div>
                <div class="quota-progress">
                  <div class="quota-progress-bar" :style="{ width: quotaPercentage + '%' }"></div>
                </div>
              </div>
            </div>

            <!-- 快捷操作 -->
            <div class="quick-actions">
              <div class="action-item" @click="handleMenuAction('go_to_profile')">
                <div class="action-icon">
                  <el-icon><User /></el-icon>
                </div>
                <span class="action-text">个人中心</span>
              </div>

              <!-- 主题切换 - 暂时注释，后续可能启用 -->
              <!-- <div class="action-item" @click="$emit('theme-toggle')">
                <div class="action-icon">
                  <el-icon>
                    <Sunny v-if="isDarkTheme"/>
                    <Moon v-else/>
                  </el-icon>
                </div>
                <span class="action-text">{{ isDarkTheme ? '浅色模式' : '深色模式' }}</span>
              </div> -->

              <!-- 分割线 -->
              <!-- <div class="menu-divider"></div> -->

              <!-- 帮助文档 -->
              <div class="action-item" @click="handleMenuAction('show_guide')">
                <div class="action-icon">
                  <el-icon><QuestionFilled /></el-icon>
                </div>
                <span class="action-text">查看引导</span>
              </div>

              <!-- API 官网 -->
              <div class="action-item" @click="handleMenuAction('open_api_site')">
                <div class="action-icon">
                  <el-icon><Link /></el-icon>
                </div>
                <span class="action-text">API 官网</span>
              </div>

              <!-- 分割线 -->
              <div class="menu-divider"></div>

              <div class="action-item logout-action" @click="handleMenuAction('logout')">
                <div class="action-icon">
                  <el-icon><SwitchButton /></el-icon>
                </div>
                <span class="action-text">退出登录</span>
              </div>
            </div>
          </div>
        </template>
      </el-dropdown>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import {
  Star,
  User,
  SwitchButton,
  ArrowDown,
  // Sunny,
  // Moon,
  QuestionFilled,
  Link
} from '@element-plus/icons-vue'
import LoginDialog from './LoginDialog.vue'
import { trackRumClick } from '@/utils/rum'

interface Props {
  isLoggedIn: boolean
  userInfo?: {
    username: string
    avatar?: string
    role?: string
  }
  quotaUsed: number
  quotaLimit: number
  defaultAvatar: string
  isDarkTheme?: boolean
  isMobile?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'logout': []
  'go-to-profile': []
  'theme-toggle': []
  'show-guide': []
  'open-api-site': []
}>()

const showLoginDialog = ref(false)

const handleShowLogin = () => {
  trackRumClick('top_bar', 'show_login_dialog')
  showLoginDialog.value = true
}

const handleAvatarClick = () => {
  emit('go-to-profile')
}

const handleMenuAction = (action: 'go_to_profile' | 'show_guide' | 'open_api_site' | 'logout') => {
  trackRumClick('user_menu', action, {
    username: props.userInfo?.username || '',
    role: props.userInfo?.role || ''
  })

  if (action === 'go_to_profile') {
    emit('go-to-profile')
  } else if (action === 'show_guide') {
    emit('show-guide')
  } else if (action === 'open_api_site') {
    emit('open-api-site')
  } else if (action === 'logout') {
    emit('logout')
  }
}

const handleLoginSuccess = () => {
  trackRumClick('auth', 'login_success')
  showLoginDialog.value = false
  window.dispatchEvent(new CustomEvent('login-success'))
}

const handleGlobalShowLogin = () => {
  trackRumClick('auth', 'show_login_dialog_by_guard')
  showLoginDialog.value = true
}

onMounted(() => {
  window.addEventListener('show-login-dialog', handleGlobalShowLogin)
})

onUnmounted(() => {
  window.removeEventListener('show-login-dialog', handleGlobalShowLogin)
})

// 计算属性
const userRoleLabel = computed(() => {
  return props.userInfo?.role || '普通用户'
})

const quotaPercentage = computed(() => {
  if (props.quotaLimit === 0) return 0
  return Math.min((props.quotaUsed / props.quotaLimit) * 100, 100)
})
</script>


<style scoped>
.user-menu {
  display: flex;
  align-items: center;
}

.user-menu :deep(.el-button--primary) {
  --el-button-bg-color: transparent;
  --el-button-border-color: transparent;
  --el-button-hover-bg-color: transparent;
  --el-button-hover-border-color: transparent;
  --el-button-active-bg-color: transparent;
  --el-button-active-border-color: transparent;
  min-width: 90px;
  height: 42px;
  padding: 0 22px;
  border: 0;
  border-radius: 12px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.24), rgba(255, 255, 255, 0) 34%),
    linear-gradient(135deg, #38bdf8 0%, #2f83ff 58%, #2b6ff2 100%);
  box-shadow:
    0 12px 24px rgba(47, 131, 255, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.34);
  color: #ffffff;
  font-size: 16px;
  font-weight: 800;
  line-height: 1;
  transition: transform 0.22s ease, box-shadow 0.22s ease, filter 0.22s ease;
}

.user-menu :deep(.el-button--primary:hover),
.user-menu :deep(.el-button--primary:focus) {
  transform: translateY(-1px);
  filter: saturate(1.08);
  box-shadow:
    0 16px 30px rgba(47, 131, 255, 0.34),
    inset 0 1px 0 rgba(255, 255, 255, 0.38);
}

.user-menu :deep(.el-button--primary:active) {
  transform: translateY(0);
  box-shadow:
    0 8px 18px rgba(47, 131, 255, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.26);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 4px 10px 4px 6px;
  border: 1px solid rgba(120, 207, 255, 0.36);
  border-radius: 999px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(237, 249, 255, 0.92)),
    linear-gradient(135deg, #f8fdff, #dff4ff);
  box-shadow:
    0 12px 24px rgba(33, 150, 243, 0.11),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);
  cursor: pointer;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.user-info:hover {
  transform: translateY(-1px);
  border-color: rgba(47, 178, 255, 0.56);
  box-shadow:
    0 14px 26px rgba(33, 150, 243, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);
}

.user-info:focus-visible {
  outline: 2px solid var(--tts-primary);
  outline-offset: 2px;
}

.dropdown-arrow {
  font-size: 14px;
  color: #2f83ff;
  transition: transform 0.3s ease;
}

.user-info:hover .dropdown-arrow {
  color: var(--tts-text-primary);
}

.username {
  font-size: var(--tts-font-sm);
  font-weight: 500;
  color: var(--tts-text-primary);
}

.modern-user-dropdown {
  position: relative;
  padding: 18px;
  min-width: 320px;
  border-radius: 24px;
  background:
    radial-gradient(circle at top right, rgba(120, 207, 255, 0.16), transparent 34%),
    radial-gradient(circle at bottom left, rgba(59, 130, 246, 0.08), transparent 30%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(245, 250, 255, 0.96));
  box-shadow:
    0 28px 60px rgba(15, 23, 42, 0.12),
    0 10px 24px rgba(47, 131, 255, 0.12);
  border: 1px solid rgba(120, 207, 255, 0.24);
  backdrop-filter: blur(18px);
  overflow: hidden;
  transition: border-radius 0.22s ease, box-shadow 0.22s ease, transform 0.22s ease;
}

.modern-user-dropdown::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.24), rgba(255, 255, 255, 0));
  pointer-events: none;
}

.user-profile-section {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
  position: relative;
  z-index: 1;
}

.user-avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.user-status-dot {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  background: var(--tts-success);
  border: 2px solid rgba(255, 255, 255, 0.96);
  border-radius: 50%;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.16);
}

.user-basic-info {
  flex: 1;
}

.user-name {
  font-size: 18px;
  font-weight: 700;
  color: var(--tts-text-primary);
  margin-bottom: 6px;
  line-height: 1.2;
}

.user-role-badge {
  font-size: 12px;
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
  padding: 4px 10px;
  border-radius: 999px;
  display: inline-block;
  border: 1px solid rgba(59, 130, 246, 0.14);
}

.quota-card {
  position: relative;
  z-index: 1;
  background:
    linear-gradient(180deg, rgba(250, 252, 255, 0.98), rgba(240, 247, 255, 0.94));
  border-radius: 20px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid rgba(125, 158, 255, 0.12);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.88),
    0 10px 24px rgba(148, 163, 184, 0.08);
}

.quota-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.quota-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f59e0b;
  border-radius: 12px;
  background: rgba(245, 158, 11, 0.1);
}

.quota-icon :deep(.el-icon) {
  font-size: 16px;
}

.quota-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--tts-text-primary);
}

.quota-numbers {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 10px;
}

.quota-used {
  font-size: 28px;
  font-weight: 800;
  color: var(--tts-primary);
  line-height: 1;
}

.quota-separator {
  color: var(--tts-text-secondary);
  font-size: 16px;
}

.quota-total {
  font-size: 15px;
  color: var(--tts-text-secondary);
}

.quota-progress {
  height: 6px;
  background: rgba(148, 163, 184, 0.16);
  border-radius: 999px;
  overflow: hidden;
}

.quota-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
  transition: width 0.3s ease;
  border-radius: inherit;
}

.quick-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
  z-index: 1;
}

.action-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 16px;
  cursor: pointer;
  transition: transform 0.22s ease, background 0.22s ease, box-shadow 0.22s ease, color 0.22s ease;
  color: var(--tts-text-primary);
}

.action-item:hover {
  background: rgba(59, 130, 246, 0.06);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
  transform: translateX(1px);
}

.action-item:focus-visible {
  outline: 2px solid var(--tts-primary);
  outline-offset: 2px;
}

.action-item.logout-action:hover {
  background: rgba(239, 68, 68, 0.08);
  color: var(--tts-error);
}

.action-item.version-item {
  cursor: default;
  opacity: 0.7;
}

.action-item.version-item:hover {
  background: transparent;
}

.action-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 12px;
  background: rgba(59, 130, 246, 0.08);
  color: #3b82f6;
  flex-shrink: 0;
}

.action-icon :deep(.el-icon) {
  font-size: 16px;
}

.action-text {
  font-size: 14px;
  font-weight: 600;
}

.menu-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(148, 163, 184, 0.22), transparent);
  margin: 8px 0;
}

@media (max-width: 768px) {
  .modern-user-dropdown {
    min-width: 280px;
    padding: 14px;
    border-radius: 20px;
  }
  
  .username {
    display: none;
  }
}

:global(.el-popper.user-menu-dropdown-popper) {
  padding: 0;
  border: 0;
  border-radius: 24px !important;
  overflow: hidden;
  background: transparent !important;
  box-shadow: none !important;
}

:global(.el-popper.user-menu-dropdown-popper[data-popper-placement*='bottom']) {
  transform-origin: top center;
}

:global(.el-popper.user-menu-dropdown-popper .modern-user-dropdown) {
  border-radius: 24px;
}
</style>
